<template>
  <div
    class="product-gallery"
    :class="`product-gallery--${responsiveLayout}`"
  >
    <div class="product-gallery__main-container">
      <transition name="fade">
        <button
          v-if="showZoomButton"
          class="product-gallery__zoom button button--dark button--round"
          type="button"
          :aria-label="$string('product.gallery.zoom')"
          @click.prevent="handleZoomClick"
        >
          <icon-plus />
        </button>
      </transition>

      <product-carousel
        class="product-gallery__main-carousel"
        :current-index="currentIndex"
        :current-namespace="currentNamespace"
        :config="config.main"
        :layout="responsiveLayout"
        :loading="loading"
        namespace="gallery"
        :product="product"
        :min-max="minMax"
        :sizes="sizes.primary"
        :secondary-sizes="sizes.secondary"
        :secondary-index="1"
        type="main"
        :zoom="zoom"
        :loop="loop"
        :transition-unmount="transitionUnmount"
        :use-loading-animation-from-index="useLoadingAnimationFromIndex"
        @splide-mounted="payload => main = payload"
        @update-current-index="handleUpdateCurrentIndex"
      />
    </div>

    <div
      v-if="showThumbnails"
      class="product-gallery__thumbnails-container"
    >
      <product-carousel
        class="product-gallery__thumbnails-carousel"
        :current-index="currentIndex"
        :current-namespace="currentNamespace"
        :config="config.thumbnails"
        :layout="responsiveLayout"
        :loading="loading"
        min-max="100-300"
        namespace="gallery"
        :product="product"
        sizes="70px"
        type="thumbnails"
        :loop="false"
        :zoom="false"
        :transition-unmount="transitionUnmount"
        @splide-mounted="payload => thumbs = payload"
        @update-current-index="handleUpdateCurrentIndex"
      />
    </div>

    <!-- Product gallery modal -->
    <overlay
      v-if="zoom"
      key="productGalleryModal"
      block-class="product-gallery-modal-overlay"
      direction="up"
      namespace="productGalleryModal"
      :show-overlay="false"
      :template="false"
      type="modal"
    >
      <template #body="overlay">
        <product-gallery-modal
          v-if="getComponentVisible('product-gallery-modal')"
          v-cloak
          :current-index="currentIndex"
          :current-namespace="currentNamespace"
          namespace="modal"
          :overlay="overlay"
          :product="product"
          @update-current-index="handleUpdateCurrentIndex"
          @update-current-namespace="handleUpdateCurrentNamespace"
        />
      </template>
    </overlay>
  </div>
</template>

<script>

/**
 * Vue: Product gallery (product-gallery)
 * -----------------------------------------------------------------------------
 * Wrapper for product gallery carousel.
 *
 * @param {Number} currentIndex - Current carousel index.
 * @param {String} currentNamespace - Current carousel namespace.
 * @param {String} layout - Product gallery layout, either `horizontal`,
 * `vertical`, `tabs`, or `grid`, supports a combination in the format
 * [mobile layout]-[desktop layout].
 * - Horizontal displays horizontal thumbnails
 * - Vertical displays vertical thumbnails
 * - Tabs displays a single image with pagination (mobile) and arrows (desktop)
 * - Grid displays all images in a grid layout
 * @param {Boolean} [loading] - Loading state.
 * @param {Object} product - Local product object.
 * @param {Object|Boolean} variant - Canvas formatted variant object.
 * @param {Boolean} [zoom] - Enable zoom functionality.
 * @param {Number} [useLoadingAnimationFromIndex] - The starting slide index
 * where the image loading animation should be used
 * @param {Boolean} [transitionUnmount] - Maintain splide state during unmount
 * for transitions
 *
 * @see https://main--65ae3323a3e5ba5998b05d23.chromatic.com/?path=/story/canvas-product-gallery--primary
 * @emits updateCurrentIndex - Emit update current carousel index event.
 * @emits updateCurrentNamespace - Emit update current carousel namespace event.
 *
 */
import { defineAsyncComponent } from 'vue'
import { mapActions, mapState } from 'vuex'

import Overlay from '~global/overlay/overlay'
import ProductCarousel from '~async/product-carousel/product-carousel'

import IconPlus from '~icons/directional-navigation/plus.svg'

import { values as breakpoints } from '~/config/breakpoints'

export default {
  name: 'ProductGallery',

  components: {
    IconPlus,
    Overlay,
    ProductCarousel,
    ProductGalleryModal: defineAsyncComponent({
      loader: () => import(
        /* webpackChunkName: 'component.product-gallery-modal' */
        '~async/product-gallery-modal/product-gallery-modal'
      ),
    }),
  },

  props: {
    currentIndex: {
      type: Number,
      default: 0,
    },
    currentNamespace: {
      type: String,
      default: 'gallery',
      validator: (value) => {
        return ['gallery', 'modal'].includes(value)
      },
    },
    layout: {
      type: String,
      default: 'horizontal',
      validator: (value) => {
        return value.split('-')
          .every((part) => [
            'grid',
            'horizontal',
            'tabs',
            'vertical',
          ].includes(part))
      },
    },
    loading: {
      type: Boolean,
      default: false,
    },
    product: {
      type: [Boolean, Object],
      default: false,
    },
    variant: {
      type: [Boolean, Object],
      default: false,
    },
    zoom: {
      type: Boolean,
      default: true,
    },
    loop: {
      type: Boolean,
      default: true,
    },
    showThumbs: {
      type: Boolean,
      default: false,
    },
    pagination: {
      type: Boolean,
      default: null,
    },
    thumbsPerPage: {
      type: Number,
      default: 5,
    },
    useLoadingAnimationFromIndex: {
      type: Number,
      default: 0,
    },
    minMax: {
      type: String,
      default: '300-1000',
    },
    transitionUnmount: {
      type: Boolean,
      default: false,
    },
  },

  emits: {
    'update-current-index': (payload) => typeof payload === 'number',
    'update-current-namespace': (payload) => ['gallery', 'modal'].includes(payload),
    'main-splide': (payload) => typeof payload === 'object',
    'thumbs-splide': (payload) => typeof payload === 'object',
  },

  data() {
    return {
      main: null,
      thumbs: null,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Compute carousel configs.
     * - The aim here is to convert the responsive layout into the breakpoints
     *   object supported by Splide.
     * @returns {Object}
     */
    config() {
      const mainConfigs = {
        grid: {
          arrows: false,
          destroy: true,
          pagination: false,
        },
        horizontal: {
          arrows: true,
          destroy: false,
          pagination: this.pagination || false,
        },
        tabs: {
          arrows: false,
          destroy: false,
          pagination: true,
          breakpoints: {
            [breakpoints.l]: {
              arrows: true,
              pagination: false,
            },
          },
        },
        vertical: {
          arrows: true,
          destroy: false,
          pagination: false,
        },
      }

      const perPage = Math.min(this.media.length, this.thumbsPerPage)
      const thumbnailsConfigs = {
        shared: {
          arrows: true,
          drag: false,
          focus: 'center',
          pagination: false,
          perMove: 1,
          perPage,
          type: 'slide',
          isNavigation: true,
        },
        horizontal: {
          gap: 'var(--spacing-m)',
        },
        vertical: {
          direction: 'ttb',
          fixedWidth: 'var(--thumbnail-size)',
          fixedHeight: 'var(--thumbnail-size)',
          gap: 'var(--splide-gap)',
          height: `calc((var(--thumbnail-size) + var(--splide-gap)) * ${perPage})`,
          trimSpace: true,
        },
        verticalBreakpoints: {},
      }

      /**
       * Set configs.
       * - No difference between mobile and desktop layouts.
       */
      if (!this.layout.includes('-')) {
        const config = {
          main: mainConfigs[this.layout],
          thumbnails: {
            ...thumbnailsConfigs.shared,
            ...thumbnailsConfigs[this.layout],
          },
        }

        if (this.layout === 'vertical') {
          config.thumbnails.breakpoints = thumbnailsConfigs.verticalBreakpoints
        }

        return config
      }

      /**
       * Set mobile and desktop layouts independently.
       */
      const mobileLayout = this.layout.split('-')[0]
      const desktopLayout = this.layout.split('-')[1]

      const config = {
        main: {
          ...mainConfigs[mobileLayout],
          breakpoints: {
            [breakpoints.l]: {
              ...mainConfigs[desktopLayout],
            },
          },
        },
        thumbnails: {
          ...thumbnailsConfigs.shared,
          ...thumbnailsConfigs[mobileLayout],
          breakpoints: {
            [breakpoints.l]: {
              ...thumbnailsConfigs[desktopLayout],
            },
          },
        },
      }

      /**
       * Add vertical layout breakpoints.
       */
      if (mobileLayout === 'vertical') {
        config.thumbnails.breakpoints = thumbnailsConfigs.verticalBreakpoints[breakpoints.m]
      }

      if (desktopLayout === 'vertical') {
        config.thumbnails.breakpoints = {
          [breakpoints.l]: {
            ...config.thumbnails.breakpoints[breakpoints.l],
            ...thumbnailsConfigs.verticalBreakpoints[breakpoints.l],
          },
          [breakpoints.xl]: thumbnailsConfigs.verticalBreakpoints[breakpoints.xl],
        }
      }

      /**
       * Return config.
       */
      return config
    },

    /**
     * Compute media array.
     * @returns {Array}
     */
    media() {
      return !this.product?.media && this.product?.images
        ? this.product.images || []
        : this.product.media || []
    },

    /**
     * Compute responsive layout based on mobile.
     * @returns {String}
     */
    responsiveLayout() {
      if (!this.layout.includes('-')) {
        return this.layout
      }

      return this.isMobile
        ? this.layout.split('-')[0]
        : this.layout.split('-')[1]
    },

    /**
     * Compute whether to show thumbnails carousel.
     * @returns {Boolean}
     */
    showThumbnails() {
      return (
        this.media.length > 1 && this.showThumbs &&
        (this.responsiveLayout === 'horizontal' || this.responsiveLayout === 'vertical')
      )
    },

    /**
     * Compute whether to show zoom button.
     * - Button is hidden when video media is active.
     * @returns {Boolean}
     */
    showZoomButton() {
      const currentMedia = this.media[this.currentIndex]

      return (
        this.zoom &&
        !this.loading &&
        currentMedia &&
        (currentMedia.mediaType === 'image' || !currentMedia.mediaType)
      )
    },

    /**
     * Compute responsive image sizes.
     * @returns {String}
     */
    sizes() {
      return {
        primary: `(min-width: ${breakpoints.l}px) 60vw, 100vw`,
        secondary: `(min-width: ${breakpoints.l}px) 30vw, 100vw`,
      }
    },
  },

  watch: {

    /**
     * Watch variant to show matching image if it exists.
     */
    variant() {
      if (!this.variant) {
        return
      }

      /**
       * Matching image with variant only works when using images array.
       * - Media items do not return associated variant ID.
       * - Media and images array item IDs do not match so it's not possible to
       *   match.
       */
      const matchedImageIndex = this.media.findIndex((mediaItem) => {
        return mediaItem.variant === this.variant.id
      })

      if (matchedImageIndex < 0) {
        return
      }

      this.$emit('update-current-index', matchedImageIndex)
    },
  },

  mounted() {
    if (this.thumbs && this.main) {
      this.main.sync(this.thumbs.splide)
    }
    this.$emit('main-splide', this.main)
    this.$emit('thumbs-splide', this.thumbs)
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      openOverlay: 'overlays/open',
    }),

    /**
     * Emit update current carousel index event.
     * @param {Number} newIndex - New media index.
     */
    handleUpdateCurrentIndex(newIndex) {
      this.$emit('update-current-index', newIndex)
    },

    /**
     * Emit update current carousel namespace event.
     * @param {Number} newNamespace - New active namespace.
     */
    handleUpdateCurrentNamespace(newNamespace) {
      this.$emit('update-current-namespace', newNamespace)
    },

    /**
     * Handle zoom click.
     */
    handleZoomClick() {
      this.openOverlay({
        component: 'product-gallery-modal',
        ignoreDismissed: true,
        namespace: 'productGalleryModal',
      })
    },
  },
}
</script>

<style lang="scss">
@import '@/components/splide';
@import './product-gallery';
@import './product-gallery-grid';
@import './product-gallery-tabs';
// Disable unused layout stylesheets
//@import './product-gallery-horizontal';
@import './product-gallery-vertical';
</style>
